import type { Combatant, WeaponProfile } from "@lancer-vtt/domain";
import { formatWeaponDamage, formatWeaponRange, weaponChips } from "../weaponFormat";
import type { classifySkirmishCard } from "./chooserClassify";
import type { ActionDraft } from "../combatInspector";
import { useRangeHover } from "../rangePreview";

/** The verdict {@link classifySkirmishCard} returns — the card's whole visual state. */
export type WeaponCardState = ReturnType<typeof classifySkirmishCard>;

/**
 * The **brutalist weapon card** — name · damage/state · range · tag chips · effect, as one
 * `.skirmish-hud-wpn` button, with the per-card greying + `title` wording that mirrors the server's
 * own rejection text.
 *
 * **Shared by {@link SkirmishChooser} and {@link PrepareChooser}, deliberately.** Prepare arms a
 * Skirmish hold by naming the SAME `weaponInstanceKey` a live Skirmish arms, and RAW gates a
 * prepare-time weapon by the SAME Ordnance window and Loading state a fire-time one is gated by
 * (`checkPreparableHold`). Two copies of this markup would drift the moment either gate moved —
 * and the drift would be invisible, because both would still render a plausible card. So the card
 * lives here once and each chooser supplies only what differs: which key is selected and what a
 * click does.
 *
 * **It mirrors, it never enforces** (CLAUDE.md: "UI mirrors the reducer's rules as affordances").
 * The `state` verdict is computed by the caller from {@link classifySkirmishCard} — the classifier
 * that imports the engine's own predicates — and this component only paints it. A card that slips
 * through is the authority's to reject; the categorized rejection toast surfaces it.
 *
 * The DOM shape (`.skirmish-hud-wpn` + `-name` / `-dmg` / `-range` / `-tags` / `-chip` / `-effect`)
 * is load-bearing: `ordnanceWindow.render.test.tsx` reads cards out of the real rendered chooser by
 * exactly these class names.
 */
export function WeaponCard({
  weapon: w,
  combatant: c,
  state,
  outOfThreat,
  selected,
  preview,
  onToggle,
}: {
  weapon: WeaponProfile;
  /** The wielder — its name/kind appear in the Ordnance / unloaded titles, so the message names the real unit. */
  combatant: Combatant;
  state: WeaponCardState;
  /**
   * The Overwatch take flow only: this weapon's THREAT falls short of the fixed target
   * (`overwatchThreatShortfall`). Greys an otherwise armable card; any `state` block wins over it,
   * since a weapon-state problem is the more actionable reason.
   */
  outOfThreat?: { threat: number; distance: number };
  selected: boolean;
  /**
   * The armed Skirmish with this weapon: hovering the card paints its reach on the board
   * (range-hover), the armed shot's other weapon showing as the fire card's do (`washAimFor`).
   */
  preview?: ActionDraft;
  /** Click handler — the caller owns the toggle-off semantics (re-clicking the armed card clears it). */
  onToggle: () => void;
}) {
  const chips = weaponChips(w);
  const shortOfTarget = state === "armable" ? outOfThreat : undefined;
  const disabled = state !== "armable" || shortOfTarget !== undefined;
  const rangeHover = useRangeHover(preview);
  return (
    <button
      {...rangeHover}
      type="button"
      className={`skirmish-hud-wpn${selected ? " is-sel" : ""}`}
      aria-pressed={selected}
      disabled={disabled}
      title={shortOfTarget ? outOfThreatTitle(shortOfTarget) : weaponCardTitle({ weapon: w, combatant: c, state })}
      onClick={onToggle}
    >
      <span className="skirmish-hud-wpn-top">
        <span className={`skirmish-hud-wpn-name${state === "destroyed" ? " is-destroyed" : ""}`}>{w.name}</span>
        <span
          className={`skirmish-hud-wpn-dmg${state === "unloaded" ? " is-unloaded" : ""}${state === "destroyed" ? " is-destroyed" : ""}`}
        >
          {shortOfTarget ? "OUT OF THREAT" : weaponCardDamageSlot(w, state)}
        </span>
      </span>
      {w.range && w.range.length > 0 && <span className="skirmish-hud-wpn-range">{formatWeaponRange(w.range)}</span>}
      {chips.length > 0 && (
        <span className="skirmish-hud-wpn-tags">
          {chips.map((t) => (
            <span key={t} className="skirmish-hud-chip">
              {t}
            </span>
          ))}
        </span>
      )}
      {w.effect && <span className="skirmish-hud-wpn-effect">{w.effect}</span>}
    </button>
  );
}

/** An out-of-threat card's tooltip: `Threat 1, target is 3 away`. */
export function outOfThreatTitle(short: { threat: number; distance: number }): string {
  return `Threat ${short.threat}, target is ${short.distance} away`;
}

/**
 * The word shown in the card's damage slot — the weapon's damage when armable, else the state word.
 * Split out (rather than inlined) so the {@link WeaponCard} body stays one readable element tree.
 */
function weaponCardDamageSlot(w: WeaponProfile, state: WeaponCardState): string {
  switch (state) {
    case "destroyed":
      return "DESTROYED";
    case "discharged":
      return "DISCHARGED";
    case "unloaded":
      return "UNLOADED";
    case "limited":
      return "DEPLETED";
    case "ordnance-window":
      return "WINDOW CLOSED";
    case "ordnance-overwatch":
    case "superheavy-overwatch":
      return "NO OVERWATCH";
    case "superheavy-skirmish":
      return "BARRAGE ONLY";
    case "armable":
      return formatWeaponDamage(w.damage);
  }
}

/**
 * The card's native tooltip — **worded off the server's own rejection text** for each state, so the
 * pre-click annotation and the post-click toast say the same thing. `undefined` on an armable card
 * (no tooltip at all, matching the pre-extraction behavior).
 */
export function weaponCardTitle(args: { weapon: WeaponProfile; combatant: Combatant; state: WeaponCardState }): string | undefined {
  const { weapon: w, combatant: c, state } = args;
  switch (state) {
    case "destroyed":
      return "destroyed — permanently excluded from Skirmish";
    case "discharged":
      return `discharged (Recharge ${w.recharge}+) — awaits ${c.name}'s next start-of-turn recharge roll`;
    case "unloaded":
      return `unloaded — reload with ${c.kind === "pilot" ? "RELOAD" : "STABILIZE"}`;
    case "limited":
      return `out of charges (Limited ${w.limited}) — depleted for the rest of the encounter`;
    // The two Ordnance timing walls (RAW p.105).
    case "ordnance-window":
      return `${w.name} is Ordnance — can only be fired before ${c.name} moves or takes any other action this turn (excepting Protocols); this turn's window has closed`;
    case "ordnance-overwatch":
      return `${w.name} is Ordnance — an Ordnance weapon can never be used for Overwatch (RAW p.105)`;
    case "superheavy-skirmish":
    case "superheavy-overwatch":
      return `${w.name} is a Superheavy weapon — it can only be fired as part of a Barrage (Core p.70)`;
    case "armable":
      return undefined;
  }
}
